// Intercom Integration Library for Synap Marketing
// App ID: b62v3i9h

// Extend Window interface for Intercom
declare global {
  interface Window {
    Intercom: any
    intercomSettings: any
    attachEvent?: (event: string, listener: () => void) => void
  }
}

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID || 'b62v3i9h'

// ===================================
// Core Intercom Functions
// ===================================

/**
 * Initialize Intercom with custom settings
 */
export function initIntercom(customSettings?: Record<string, any>) {
  window.intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: false,
    alignment: 'right',
    horizontal_padding: 20,
    vertical_padding: 20,
    ...customSettings,
  }

  // Load Intercom script
  const w = window as any
  const ic = w.Intercom
  if (typeof ic === 'function') {
    ic('reattach_activator')
    ic('update', w.intercomSettings)
  } else {
    const d = document
    const i = function () {
      (i as any).c(arguments)
    } as any
    i.q = []
    i.c = function (args: any) {
      i.q.push(args)
    }
    w.Intercom = i
    const l = function () {
      const s = d.createElement('script')
      s.type = 'text/javascript'
      s.async = true
      s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
      const x = d.getElementsByTagName('script')[0]
      x?.parentNode?.insertBefore(s, x)
    }
    if (document.readyState === 'complete') {
      l()
    } else if (w.attachEvent) {
      w.attachEvent('onload', l)
    } else {
      w.addEventListener('load', l, false)
    }
  }
}

/**
 * Shutdown Intercom (for cleanup)
 */
export function shutdownIntercom() {
  if (window.Intercom) {
    window.Intercom('shutdown')
  }
}

/**
 * Update Intercom with new settings or user data
 */
export function updateIntercom(data: Record<string, any>) {
  if (window.Intercom) {
    window.Intercom('update', data)
  }
}

/**
 * Boot Intercom for identified users
 */
export function bootIntercom(userData: {
  user_id?: string
  email?: string
  name?: string
  created_at?: number
  company?: {
    company_id: string
    name: string
    plan?: string
  }
  [key: string]: any
}) {
  if (window.Intercom) {
    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      ...userData,
    })
  }
}

// ===================================
// Event Tracking Functions
// ===================================

/**
 * Track a custom event in Intercom
 */
export function trackEvent(eventName: string, metadata?: Record<string, any>) {
  if (window.Intercom) {
    window.Intercom('trackEvent', eventName, metadata)
  }
}

/**
 * Track page view event
 */
export function trackPageView(
  pageName: string,
  category: 'landing' | 'value' | 'pricing' | 'auth' | 'app',
  metadata?: Record<string, any>
) {
  trackEvent('page_viewed', {
    page_name: pageName,
    page_category: category,
    url: window.location.href,
    referrer: document.referrer,
    ...metadata,
  })
}

/**
 * Track CTA button click
 */
export function trackCTAClick(
  buttonText: string,
  location: string,
  destination?: string
) {
  trackEvent('cta_clicked', {
    button_text: buttonText,
    button_location: location,
    destination: destination || 'unknown',
    page_url: window.location.pathname,
  })
}

/**
 * Track video watched
 */
export function trackVideoWatched(
  videoName: string,
  completionRate: number,
  watchTimeSeconds: number
) {
  trackEvent('video_watched', {
    video_name: videoName,
    completion_rate: completionRate,
    watch_time_seconds: watchTimeSeconds,
  })
}

/**
 * Track feature section viewed (for /value page)
 */
export function trackFeatureViewed(
  sectionName: string,
  timeOnSectionSeconds: number
) {
  trackEvent('feature_section_viewed', {
    section_name: sectionName,
    time_on_section: timeOnSectionSeconds,
  })
}

/**
 * Track pricing interaction
 */
export function trackPricingInteraction(
  action: 'plan_viewed' | 'plan_compared' | 'toggle_switched' | 'cta_clicked',
  metadata: {
    plan_name?: string
    plans_compared?: string[]
    billing_type?: 'monthly' | 'annual'
    [key: string]: any
  }
) {
  trackEvent(`pricing_${action}`, {
    ...metadata,
    page_url: window.location.pathname,
  })
}

/**
 * Track qualification completion
 */
export function trackQualification(data: {
  team_size: string
  budget_range?: string
  primary_goal?: string
  current_tools?: string[]
  industry?: string
}) {
  // Calculate lead score based on qualification data
  let score = 0

  // Team size scoring
  const teamSizeScores: Record<string, number> = {
    '1': 10,
    '2-5': 20,
    '6-20': 40,
    '21-50': 60,
    '50+': 80,
  }
  score += teamSizeScores[data.team_size] || 10

  // Budget scoring
  const budgetScores: Record<string, number> = {
    'Under $5k': 10,
    '$5k-20k': 30,
    '$20k-100k': 50,
    '$100k+': 70,
  }
  if (data.budget_range) {
    score += budgetScores[data.budget_range] || 10
  }

  trackEvent('qualification_completed', {
    ...data,
    lead_score: Math.min(score, 100),
  })

  // Update visitor attributes
  updateIntercom({
    qualification_status: score >= 50 ? 'sql' : 'mql',
    lead_score: Math.min(score, 100),
    team_size: data.team_size,
    primary_goal: data.primary_goal,
  })
}

/**
 * Track demo request
 */
export function trackDemoRequest(data: {
  preferred_time?: string
  use_case?: string
  company_size?: string
  email?: string
}) {
  trackEvent('demo_requested', {
    ...data,
    source_page: window.location.pathname,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track trial start
 */
export function trackTrialStart(data: {
  plan_selected: string
  billing_type: 'monthly' | 'annual'
  source_page?: string
}) {
  trackEvent('trial_started', {
    ...data,
    source_page: data.source_page || window.location.pathname,
    timestamp: new Date().toISOString(),
  })
}

// ===================================
// Messenger Actions
// ===================================

/**
 * Show the Intercom messenger
 */
export function showMessenger() {
  if (window.Intercom) {
    window.Intercom('show')
  }
}

/**
 * Hide the Intercom messenger
 */
export function hideMessenger() {
  if (window.Intercom) {
    window.Intercom('hide')
  }
}

/**
 * Show messenger with pre-populated message
 */
export function showNewMessage(message?: string) {
  if (window.Intercom) {
    if (message) {
      window.Intercom('showNewMessage', message)
    } else {
      window.Intercom('showNewMessage')
    }
  }
}

/**
 * Show specific Intercom content (articles, news, etc.)
 */
export function showIntercomContent(
  type: 'article' | 'news' | 'survey',
  id: string
) {
  if (window.Intercom) {
    window.Intercom('showArticle', id) // For articles
    // Note: Use 'showNews' for news, 'showSurvey' for surveys
  }
}

/**
 * Start a product tour
 */
export function startProductTour(tourId: string) {
  if (window.Intercom) {
    window.Intercom('startTour', tourId)
  }
}

// ===================================
// Visitor Attributes
// ===================================

/**
 * Update visitor/user attributes
 */
export function setVisitorAttributes(attributes: {
  visitor_score?: number
  pages_viewed?: number
  most_viewed_feature?: string
  pricing_interest_level?: 'low' | 'medium' | 'high'
  demo_requested?: boolean
  qualification_status?: 'unqualified' | 'mql' | 'sql'
  source_campaign?: string
  industry_interest?: string
  [key: string]: any
}) {
  updateIntercom(attributes)
}

// ===================================
// UTM and Attribution Tracking
// ===================================

/**
 * Get URL parameters for attribution
 */
export function getAttributionData(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    referrer: document.referrer,
  }
}

/**
 * Track initial visit with attribution
 */
export function trackInitialVisit() {
  const attribution = getAttributionData()

  trackEvent('initial_visit', {
    ...attribution,
    landing_page: window.location.pathname,
    timestamp: new Date().toISOString(),
  })

  // Store attribution in visitor data
  setVisitorAttributes({
    source_campaign: attribution.utm_campaign,
    first_visit_page: window.location.pathname,
  })
}

// ===================================
// React Hook for Intercom
// ===================================

import { useEffect, useCallback } from 'react'

interface UseIntercomOptions {
  pageName: string
  pageCategory: 'landing' | 'value' | 'pricing' | 'auth' | 'app'
  customSettings?: Record<string, any>
}

/**
 * React hook for Intercom integration
 */
export function useIntercom(options: UseIntercomOptions) {
  const { pageName, pageCategory, customSettings } = options

  useEffect(() => {
    // Initialize Intercom on mount
    initIntercom(customSettings)

    // Track page view
    trackPageView(pageName, pageCategory)

    // Cleanup on unmount
    return () => {
      // Don't shutdown - just update that user left page
      trackEvent('page_exited', {
        page_name: pageName,
        time_on_page: Math.floor(performance.now() / 1000),
      })
    }
  }, [pageName, pageCategory])

  // Memoized tracking functions
  const trackCTA = useCallback(
    (buttonText: string, location: string, destination?: string) => {
      trackCTAClick(buttonText, location, destination)
    },
    []
  )

  const trackVideo = useCallback(
    (videoName: string, completionRate: number, watchTime: number) => {
      trackVideoWatched(videoName, completionRate, watchTime)
    },
    []
  )

  const trackFeature = useCallback(
    (sectionName: string, timeOnSection: number) => {
      trackFeatureViewed(sectionName, timeOnSection)
    },
    []
  )

  const showChat = useCallback((message?: string) => {
    showNewMessage(message)
  }, [])

  return {
    trackCTA,
    trackVideo,
    trackFeature,
    showChat,
    trackEvent,
    trackPricingInteraction,
    trackQualification,
    trackDemoRequest,
    trackTrialStart,
    setVisitorAttributes,
    showMessenger,
    hideMessenger,
    startProductTour,
  }
}

// ===================================
// Page-Specific Welcome Messages
// ===================================

export const WELCOME_MESSAGES = {
  landing: `Welcome to Synap - AI-powered marketing that works while you sleep.

Whether you're exploring our platform or ready to see it in action, I'm here to help you:
- Learn how 16 AI agents can transform your marketing
- See pricing that scales with your success
- Get a personalized demo in under 15 minutes

What would you like to know?`,

  value: `Hey! I see you're exploring our features.

I can help you:
- Compare Synap with tools you're already using
- Understand how our AI agents work together
- See specific use cases for your industry

What are you most curious about?`,

  pricing: `Great to see you on our pricing page!

I'm here to help you find the perfect plan. I can:
- Explain what's included in each tier
- Calculate your potential ROI
- Connect you with sales for custom pricing

What would be most helpful?`,
}
